import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Snackbar,
  CircularProgress,
  Tabs,
  Tab,
  Card,
  CardContent,
  Grid,
  MenuItem,
  FormControlLabel,
  Switch,
  List,
  ListItem,
  ListItemText,
  Divider
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Assessment as AssessmentIcon,
  ToggleOn as ToggleOnIcon,
  ToggleOff as ToggleOffIcon,
  PlayArrow as TestIcon
} from '@mui/icons-material';
import api from '../../services/api';

const AssignmentRulesManager = () => {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [analyticsDialogOpen, setAnalyticsDialogOpen] = useState(false);
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [currentRule, setCurrentRule] = useState(null);
  const [currentAnalytics, setCurrentAnalytics] = useState(null);
  const [testResults, setTestResults] = useState(null);
  const [includeInactive, setIncludeInactive] = useState(() => {
    const saved = localStorage.getItem('assignmentRules_includeInactive');
    return saved ? JSON.parse(saved) : false;
  });
  const [tabValue, setTabValue] = useState(0);
  const [therapists, setTherapists] = useState([]);
  const [allUsers, setAllUsers] = useState([]);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    isActive: true,
    strategy: {
      type: 'auto',
      algorithm: 'round_robin',
      respectCapacity: true,
      maxCapacityOverride: null,
      matchCriteria: [],
      targetTherapistId: null,
      skipAutoAssignmentCheck: false,
      fallbackStrategy: null
    },
    eligibleTherapists: {
      serviceGroupRestriction: true,
      requiredSpecializations: [],
      minimumExperience: null
    }
  });

  // Test simulation state
  const [testData, setTestData] = useState({
    userId: '',
    serviceGroupId: ''
  });

  useEffect(() => {
    fetchRules();
    fetchUsers();
  }, [includeInactive]);

  const fetchUsers = async () => {
    try {
      const response = await api.get('/admin/users');
      const users = response.data.users || response.data || [];
      setAllUsers(users.filter(u => u.status === 'Active'));
      setTherapists(users.filter(u => u.status === 'Active' && u.role?.includes('therapist')));
    } catch (err) {
      console.error('Error fetching users for assignment rule pickers:', err);
    }
  };

  useEffect(() => {
    localStorage.setItem('assignmentRules_includeInactive', JSON.stringify(includeInactive));
  }, [includeInactive]);

  const fetchRules = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/admin/assignment-rules?includeInactive=${includeInactive}`);
      setRules(response.data.rules || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching rules:', err);
      setError(err.response?.data?.message || 'Error fetching assignment rules');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (rule = null) => {
    if (rule) {
      setCurrentRule(rule);
      setFormData({
        name: rule.name,
        slug: rule.slug,
        description: rule.description || '',
        isActive: rule.isActive,
        strategy: {
          type: rule.strategy.type,
          algorithm: rule.strategy.algorithm || 'round_robin',
          respectCapacity: rule.strategy.respectCapacity !== false,
          maxCapacityOverride: rule.strategy.maxCapacityOverride || null,
          matchCriteria: rule.strategy.matchCriteria || [],
          targetTherapistId: rule.strategy.targetTherapistId || null,
          skipAutoAssignmentCheck: rule.strategy.skipAutoAssignmentCheck || false,
          fallbackStrategy: rule.strategy.fallbackStrategy || null
        },
        eligibleTherapists: {
          serviceGroupRestriction: rule.eligibleTherapists?.serviceGroupRestriction !== false,
          requiredSpecializations: rule.eligibleTherapists?.requiredSpecializations || [],
          minimumExperience: rule.eligibleTherapists?.minimumExperience || null
        }
      });
    } else {
      setCurrentRule(null);
      setFormData({
        name: '',
        slug: '',
        description: '',
        isActive: true,
        strategy: {
          type: 'auto',
          algorithm: 'round_robin',
          respectCapacity: true,
          maxCapacityOverride: null,
          matchCriteria: [],
          targetTherapistId: null,
          skipAutoAssignmentCheck: false,
          fallbackStrategy: null
        },
        eligibleTherapists: {
          serviceGroupRestriction: true,
          requiredSpecializations: [],
          minimumExperience: null
        }
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setCurrentRule(null);
  };

  const handleSubmit = async () => {
    try {
      const slug = formData.slug || formData.name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');

      const payload = {
        ...formData,
        slug
      };

      if (currentRule) {
        await api.put(`/admin/assignment-rules/${currentRule._id}`, payload);
        setSuccess('Assignment rule updated successfully');
      } else {
        await api.post('/admin/assignment-rules', payload);
        setSuccess('Assignment rule created successfully');
      }
      handleCloseDialog();
      fetchRules();
    } catch (err) {
      console.error('Error saving rule:', err);
      setError(err.response?.data?.message || 'Error saving rule');
    }
  };

  const handleToggleActive = async (ruleId) => {
    try {
      await api.patch(`/admin/assignment-rules/${ruleId}/toggle-active`);
      setSuccess('Rule status updated successfully');
      fetchRules();
    } catch (err) {
      console.error('Error toggling rule:', err);
      setError(err.response?.data?.message || 'Error toggling rule status');
    }
  };

  const handleDelete = async (ruleId) => {
    if (!window.confirm('Are you sure you want to deactivate this rule?')) {
      return;
    }

    try {
      await api.delete(`/admin/assignment-rules/${ruleId}`);
      setSuccess('Rule deactivated successfully');
      fetchRules();
    } catch (err) {
      console.error('Error deleting rule:', err);
      setError(err.response?.data?.message || 'Error deleting rule');
    }
  };

  const handleViewAnalytics = async (ruleId) => {
    try {
      const response = await api.get(`/admin/assignment-rules/${ruleId}/analytics`);
      setCurrentAnalytics(response.data.analytics);
      setAnalyticsDialogOpen(true);
    } catch (err) {
      console.error('Error fetching analytics:', err);
      setError(err.response?.data?.message || 'Error fetching analytics');
    }
  };

  const handleOpenTestDialog = (rule) => {
    setCurrentRule(rule);
    setTestData({ userId: '', serviceGroupId: '' });
    setTestResults(null);
    setTestDialogOpen(true);
  };

  const handleRunTest = async () => {
    try {
      const response = await api.post(`/admin/assignment-rules/${currentRule._id}/test`, testData);
      setTestResults(response.data.simulation);
      setSuccess('Test simulation completed');
    } catch (err) {
      console.error('Error running test:', err);
      setError(err.response?.data?.message || 'Error running test simulation');
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4">Therapist Assignment Rules</Typography>
        <Box>
          <FormControlLabel
            control={
              <Switch
                checked={includeInactive}
                onChange={(e) => setIncludeInactive(e.target.checked)}
              />
            }
            label="Show Inactive"
            sx={{ mr: 2 }}
          />
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Create New Rule
          </Button>
        </Box>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Strategy</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Usage Stats</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rules.map((rule) => (
              <TableRow key={rule._id}>
                <TableCell>{rule.name}</TableCell>
                <TableCell>
                  <Chip label={rule.strategy?.type} size="small" color="primary" variant="outlined" />
                  {rule.strategy?.algorithm && (
                    <Chip label={rule.strategy.algorithm} size="small" sx={{ ml: 1 }} />
                  )}
                </TableCell>
                <TableCell>
                  <Chip
                    label={rule.isActive ? 'Active' : 'Inactive'}
                    color={rule.isActive ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  {rule.stats?.totalAttempts || 0} attempts / {rule.stats?.successfulMatches || 0} successes
                </TableCell>
                <TableCell>
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => handleOpenDialog(rule)}
                    title="Edit"
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    color={rule.isActive ? 'warning' : 'success'}
                    onClick={() => handleToggleActive(rule._id)}
                    title={rule.isActive ? 'Deactivate' : 'Activate'}
                  >
                    {rule.isActive ? <ToggleOffIcon /> : <ToggleOnIcon />}
                  </IconButton>
                  <IconButton
                    size="small"
                    color="info"
                    onClick={() => handleViewAnalytics(rule._id)}
                    title="View Analytics"
                  >
                    <AssessmentIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    color="secondary"
                    onClick={() => handleOpenTestDialog(rule)}
                    title="Test Rule"
                  >
                    <TestIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleDelete(rule._id)}
                    title="Delete"
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {currentRule ? 'Edit Assignment Rule' : 'Create New Assignment Rule'}
        </DialogTitle>
        <DialogContent>
          <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} sx={{ mb: 2 }}>
            <Tab label="Basic Info" />
            <Tab label="Strategy" />
            <Tab label="Eligibility" />
          </Tabs>

          {tabValue === 0 && (
            <Box sx={{ mt: 2 }}>
              <TextField
                fullWidth
                label="Rule Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                sx={{ mb: 2 }}
                required
              />
              <TextField
                fullWidth
                label="Slug"
                value={formData.slug}
                onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                sx={{ mb: 2 }}
                helperText="Leave empty to auto-generate from name"
              />
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                sx={{ mb: 2 }}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  />
                }
                label="Active"
              />
            </Box>
          )}

          {tabValue === 1 && (
            <Box sx={{ mt: 2 }}>
              <TextField
                fullWidth
                select
                label="Strategy Type"
                value={formData.strategy.type}
                onChange={(e) => setFormData({
                  ...formData,
                  strategy: { ...formData.strategy, type: e.target.value }
                })}
                sx={{ mb: 2 }}
                required
              >
                <MenuItem value="auto">Auto Assignment</MenuItem>
                <MenuItem value="manual">Manual Assignment</MenuItem>
                <MenuItem value="waitlist">Add to Waitlist</MenuItem>
                <MenuItem value="specialized_match">Specialized Match</MenuItem>
                <MenuItem value="specific_therapist">Specific Therapist</MenuItem>
              </TextField>

              {formData.strategy.type === 'specific_therapist' && (
                <>
                  <TextField
                    fullWidth
                    select
                    label="Target Therapist"
                    value={formData.strategy.targetTherapistId || ''}
                    onChange={(e) => setFormData({
                      ...formData,
                      strategy: { ...formData.strategy, targetTherapistId: e.target.value || null }
                    })}
                    sx={{ mb: 2 }}
                    required
                    helperText="The specific therapist to assign new clients to"
                  >
                    <MenuItem value="">Select a therapist...</MenuItem>
                    {therapists.map(t => (
                      <MenuItem key={t._id} value={t._id}>
                        {t.name} ({t.email}) - {t.therapistInfo?.assignedClientCount || 0}/{t.therapistInfo?.maxClientCapacity || 20} clients
                      </MenuItem>
                    ))}
                  </TextField>

                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.strategy.respectCapacity}
                        onChange={(e) => setFormData({
                          ...formData,
                          strategy: { ...formData.strategy, respectCapacity: e.target.checked }
                        })}
                      />
                    }
                    label="Respect Therapist Capacity Limits"
                    sx={{ mb: 2 }}
                  />

                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.strategy.skipAutoAssignmentCheck}
                        onChange={(e) => setFormData({
                          ...formData,
                          strategy: { ...formData.strategy, skipAutoAssignmentCheck: e.target.checked }
                        })}
                      />
                    }
                    label="Skip Auto-Assignment Check (allow assignment even if therapist has auto-assignments disabled)"
                    sx={{ mb: 2 }}
                  />
                </>
              )}

              {(formData.strategy.type === 'auto' || formData.strategy.type === 'specialized_match') && (
                <>
                  <TextField
                    fullWidth
                    select
                    label="Selection Algorithm"
                    value={formData.strategy.algorithm}
                    onChange={(e) => setFormData({
                      ...formData,
                      strategy: { ...formData.strategy, algorithm: e.target.value }
                    })}
                    sx={{ mb: 2 }}
                  >
                    <MenuItem value="round_robin">Round Robin</MenuItem>
                    <MenuItem value="least_clients">Least Clients</MenuItem>
                    <MenuItem value="best_match">Best Match</MenuItem>
                  </TextField>

                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.strategy.respectCapacity}
                        onChange={(e) => setFormData({
                          ...formData,
                          strategy: { ...formData.strategy, respectCapacity: e.target.checked }
                        })}
                      />
                    }
                    label="Respect Therapist Capacity Limits"
                    sx={{ mb: 2 }}
                  />

                  {!formData.strategy.respectCapacity && (
                    <TextField
                      fullWidth
                      type="number"
                      label="Max Capacity Override"
                      value={formData.strategy.maxCapacityOverride || ''}
                      onChange={(e) => setFormData({
                        ...formData,
                        strategy: { ...formData.strategy, maxCapacityOverride: parseInt(e.target.value) || null }
                      })}
                      sx={{ mb: 2 }}
                    />
                  )}
                </>
              )}

              <TextField
                fullWidth
                select
                label="Fallback Strategy (if no match)"
                value={formData.strategy.fallbackStrategy?.type || ''}
                onChange={(e) => setFormData({
                  ...formData,
                  strategy: {
                    ...formData.strategy,
                    fallbackStrategy: e.target.value ? { type: e.target.value } : null
                  }
                })}
                sx={{ mb: 2 }}
              >
                <MenuItem value="">None</MenuItem>
                <MenuItem value="waitlist">Add to Waitlist</MenuItem>
                <MenuItem value="default_auto">Use Legacy Auto-Assignment</MenuItem>
                <MenuItem value="clinical_supervisor_review">Clinical Supervisor Review</MenuItem>
                <MenuItem value="specific_user">Specific User (Failover)</MenuItem>
              </TextField>

              {formData.strategy.fallbackStrategy?.type === 'specific_user' && (
                <TextField
                  fullWidth
                  select
                  label="Failover User"
                  value={formData.strategy.fallbackStrategy?.specificUserId || ''}
                  onChange={(e) => setFormData({
                    ...formData,
                    strategy: {
                      ...formData.strategy,
                      fallbackStrategy: {
                        ...formData.strategy.fallbackStrategy,
                        specificUserId: e.target.value || null
                      }
                    }
                  })}
                  sx={{ mb: 2 }}
                  required
                  helperText="User to failover to (therapist or supervisor). If they have therapist role and capacity, client is assigned directly. Otherwise, queued for manual review."
                >
                  <MenuItem value="">Select a user...</MenuItem>
                  {allUsers.filter(u => u.role?.includes('therapist') || u.role?.includes('clinical_supervisor')).map(u => (
                    <MenuItem key={u._id} value={u._id}>
                      {u.name} ({u.email}) - {u.role?.join(', ')}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            </Box>
          )}

          {tabValue === 2 && (
            <Box sx={{ mt: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.eligibleTherapists.serviceGroupRestriction}
                    onChange={(e) => setFormData({
                      ...formData,
                      eligibleTherapists: {
                        ...formData.eligibleTherapists,
                        serviceGroupRestriction: e.target.checked
                      }
                    })}
                  />
                }
                label="Restrict to Service Group"
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                label="Required Specializations (comma-separated)"
                value={formData.eligibleTherapists.requiredSpecializations.join(', ')}
                onChange={(e) => setFormData({
                  ...formData,
                  eligibleTherapists: {
                    ...formData.eligibleTherapists,
                    requiredSpecializations: e.target.value.split(',').map(s => s.trim()).filter(Boolean)
                  }
                })}
                sx={{ mb: 2 }}
                helperText="e.g., trauma, PTSD, anxiety"
              />

              <TextField
                fullWidth
                type="number"
                label="Minimum Years of Experience"
                value={formData.eligibleTherapists.minimumExperience || ''}
                onChange={(e) => setFormData({
                  ...formData,
                  eligibleTherapists: {
                    ...formData.eligibleTherapists,
                    minimumExperience: parseInt(e.target.value) || null
                  }
                })}
                sx={{ mb: 2 }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            {currentRule ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Analytics Dialog */}
      <Dialog open={analyticsDialogOpen} onClose={() => setAnalyticsDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Rule Analytics</DialogTitle>
        <DialogContent>
          {currentAnalytics && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="h6">{currentAnalytics.ruleName}</Typography>
                <Chip label={currentAnalytics.strategy} size="small" sx={{ mt: 1 }} />
              </Grid>
              <Grid item xs={6}>
                <Card>
                  <CardContent>
                    <Typography color="textSecondary">Total Attempts</Typography>
                    <Typography variant="h4">{currentAnalytics.stats.totalAttempts}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6}>
                <Card>
                  <CardContent>
                    <Typography color="textSecondary">Successful Matches</Typography>
                    <Typography variant="h4">{currentAnalytics.stats.successfulMatches}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6}>
                <Card>
                  <CardContent>
                    <Typography color="textSecondary">Success Rate</Typography>
                    <Typography variant="h4">{currentAnalytics.metrics.successRate}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6}>
                <Card>
                  <CardContent>
                    <Typography color="textSecondary">Fallback Rate</Typography>
                    <Typography variant="h4">{currentAnalytics.metrics.fallbackRate}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAnalyticsDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Test Simulation Dialog */}
      <Dialog open={testDialogOpen} onClose={() => setTestDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Test Assignment Rule: {currentRule?.name}</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="User ID (to test assignment for)"
              value={testData.userId}
              onChange={(e) => setTestData({ ...testData, userId: e.target.value })}
              sx={{ mb: 2 }}
              required
            />
            <TextField
              fullWidth
              label="Service Group ID"
              value={testData.serviceGroupId}
              onChange={(e) => setTestData({ ...testData, serviceGroupId: e.target.value })}
              sx={{ mb: 2 }}
              required
            />
            <Button
              variant="contained"
              onClick={handleRunTest}
              disabled={!testData.userId || !testData.serviceGroupId}
              startIcon={<TestIcon />}
            >
              Run Test
            </Button>

            {testResults && (
              <Box sx={{ mt: 3 }}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6">Test Results</Typography>

                {testResults.strategy === 'specific_therapist' ? (
                  // Specific therapist test results
                  <Box sx={{ mt: 2 }}>
                    <Alert severity={testResults.wouldAssign ? 'success' : 'warning'} sx={{ mb: 2 }}>
                      {testResults.wouldAssign
                        ? `Would assign to ${testResults.targetTherapist?.name}`
                        : 'Assignment would fail — fallback would be used'}
                    </Alert>

                    {testResults.targetTherapist && (
                      <Typography variant="subtitle1" sx={{ mb: 1 }}>
                        Target: {testResults.targetTherapist.name} ({testResults.targetTherapist.email})
                      </Typography>
                    )}

                    <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Validation Checks:</Typography>
                    <List dense>
                      {testResults.checks && Object.entries(testResults.checks).map(([key, check]) => (
                        <ListItem key={key}>
                          <ListItemText
                            primary={`${check.pass ? '\u2705' : '\u274C'} ${key}`}
                            secondary={check.detail}
                          />
                        </ListItem>
                      ))}
                    </List>

                    {testResults.fallbackInfo && (
                      <>
                        <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Fallback Configuration:</Typography>
                        <Typography variant="body2">Type: {testResults.fallbackInfo.type}</Typography>
                        {testResults.fallbackInfo.user && (
                          <Typography variant="body2">
                            User: {testResults.fallbackInfo.user.name} ({testResults.fallbackInfo.user.email}) —
                            Roles: {testResults.fallbackInfo.user.roles?.join(', ')} —
                            Capacity: {testResults.fallbackInfo.user.capacity}
                          </Typography>
                        )}
                      </>
                    )}
                  </Box>
                ) : (
                  // Standard strategy test results
                  <>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                      <Grid item xs={6}>
                        <Card>
                          <CardContent>
                            <Typography color="textSecondary">Total Eligible</Typography>
                            <Typography variant="h4">{testResults.totalEligible}</Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                      <Grid item xs={6}>
                        <Card>
                          <CardContent>
                            <Typography color="textSecondary">Available (Not at Capacity)</Typography>
                            <Typography variant="h4">{testResults.totalAvailable}</Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    </Grid>

                    <Typography variant="subtitle1" sx={{ mt: 3, mb: 1 }}>Eligible Therapists:</Typography>
                    <List>
                      {testResults.eligibleTherapists?.map((therapist) => (
                        <ListItem key={therapist.id}>
                          <ListItemText
                            primary={`${therapist.name} (${therapist.email})`}
                            secondary={`Clients: ${therapist.assignedClients}/${therapist.maxCapacity} | Experience: ${therapist.yearsOfExperience} years | Specializations: ${therapist.specializations.join(', ') || 'None'}`}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </>
                )}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTestDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Notifications */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
      >
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess(null)}
      >
        <Alert severity="success" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AssignmentRulesManager;
